// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductQuoteButton from "@awc/App/Components/Product/ProductQuoteButton";
import { QtyFieldStyles } from "@awc/App/Components/Qty/QtyField";
import { CustomAppState } from "@awc/App/Store/Reducers";
import { isProductQuoteRequired } from "@awc/App/Store/Selectors/ProductSelectors";
import ProductAddToCartButton from "@awc/Overrides/Components/ProductAddToCartButton";
import ProductPrice, { ProductPriceStyles } from "@awc/Overrides/Components/ProductPrice";
import ProductQuantityOrdered from "@awc/Overrides/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect, {
    ProductUnitOfMeasureSelectStyles,
} from "@awc/Overrides/Components/ProductUnitOfMeasureSelect";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React from "react";
import { connect } from "react-redux";

const mapStateToProps = (state: CustomAppState) => {
    return {
        configurationSelection: state.pages.productDetails.configurationSelection,
        configurationCompleted: state.pages.productDetails.configurationCompleted,
        variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
        subscription: state.pages.productDetails.subscription,
    };
};

type Props = HasProductContext & ReturnType<typeof mapStateToProps>;

export interface BarginBinQuickBuyStyles {
    qtyWrapper?: InjectableCss;
    buyBtn?: ButtonPresentationProps;
    productPriceStyles?: ProductPriceStyles;
    variantOptions?: InjectableCss;
    priceAndQtyWrapper?: InjectableCss;
    productInfoWrapper?: InjectableCss;
    lengthWrapper?: InjectableCss;
    unitOfMeasureSelect?: ProductUnitOfMeasureSelectStyles;
    title?: TypographyPresentationProps;
    subTitle?: TypographyPresentationProps;
    productQuantityOrderedStyles?: QtyFieldStyles;
}

export const barginBinQuickBuyStyles: BarginBinQuickBuyStyles = {};

const styles = barginBinQuickBuyStyles;

const BarginBinQuickBuy = ({
    productContext: { productInfo, product },
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    productContext,
}: Props) => {
    if (!product || !productInfo) {
        return null;
    }

    return (
        <StyledWrapper>
            <Typography variant="h2" {...styles.title}>
                {translate("Quick Buy: Purchase standard put-ups of {0}", product.productTitle)}
            </Typography>
            <StyledWrapper {...styles.productInfoWrapper}>
                <Typography {...styles.subTitle}>{siteMessage("BargainBin_QuickBuyMessage")}</Typography>

                <StyledWrapper {...styles.variantOptions}>
                    {createWidgetElement("ProductDetails/ProductDetailsVariantOptions", {
                        fields: {},
                        isBargainBinPage: true,
                    } as any)}
                    <StyledWrapper {...styles.lengthWrapper}>
                        <ProductUnitOfMeasureSelect
                            labelOverride={translate("Length:")}
                            extendedStyles={styles.unitOfMeasureSelect}
                            displayPack={true}
                        />
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.priceAndQtyWrapper}>
                    <StyledWrapper {...styles.qtyWrapper}>
                        <VisuallyHidden as="label">{translate("Quantity")}</VisuallyHidden>
                        <ProductQuantityOrdered
                            showLabel={true}
                            labelOverride={translate("Quantity")}
                            data-test-selector="productQty"
                            configurationSelection={configurationSelection}
                            configurationCompleted={configurationCompleted}
                            variantSelectionCompleted={variantSelectionCompleted}
                            extendedStyles={styles.productQuantityOrderedStyles}
                        />
                    </StyledWrapper>

                    {!productInfo.failedToLoadPricing && (
                        <>
                            {isProductQuoteRequired(product, productInfo) ? (
                                <ProductQuoteButton extendedStyles={styles.buyBtn} />
                            ) : (
                                <ProductAddToCartButton
                                    extendedStyles={styles.buyBtn}
                                    configurationSelection={configurationSelection}
                                    configurationCompleted={configurationCompleted}
                                    variantSelectionCompleted={variantSelectionCompleted}
                                    subscription={subscription}
                                />
                            )}
                        </>
                    )}

                    <ProductPrice
                        product={productContext}
                        showInvalidPriceMessage={true}
                        showLabel={false}
                        showPack={true}
                        showUnitOfMeasure={false}
                        extendedStyles={styles.productPriceStyles}
                    />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(withProductContext(BarginBinQuickBuy));
